(() => {
  'use strict';

  const SELECTORS = {
    root: '[data-ln-ramadan]',
    input: '[data-city-input]',
    submit: '[data-city-submit]',
    results: '[data-city-results]',
    status: '[data-status]',
    shell: '[data-calendar-shell]',
    body: '[data-calendar-body]'
  };

  const CACHE_VERSION = 'v1';
  const CALENDAR_CACHE_MS = 7 * 24 * 60 * 60 * 1000;

  const boot = (scope = document) => scope.querySelectorAll(SELECTORS.root).forEach(initRamadanTool);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', event => boot(event.target));

  function initRamadanTool(root) {
    if (root.dataset.lnInitialized === 'true') return;
    root.dataset.lnInitialized = 'true';

    const state = {
      root,
      worker: normalizeWorkerUrl(root.dataset.workerUrl || ''),
      city: null,
      calendar: null,
      searchTimer: null,
      countdownTimer: null
    };

    const els = mapElements(root);

    if (!state.worker || state.worker.includes('YOUR-WORKER')) {
      setStatus(els, 'Configuration requise : renseignez l’URL du Worker Cloudflare dans les réglages de la section.', true);
      return;
    }

    bindEvents(state, els);
    loadSeasonMeta(state, els).catch(() => {});

    const params = new URLSearchParams(location.search);
    const requestedCity = params.get('ville');
    const defaultCity = requestedCity || root.dataset.defaultCity || 'Paris';

    els.input.value = defaultCity;
    resolveAndLoad(defaultCity, state, els, { updateUrl: false });
  }

  function mapElements(root) {
    const q = (s) => root.querySelector(s);

    return {
      input: q(SELECTORS.input),
      submit: q(SELECTORS.submit),
      results: q(SELECTORS.results),
      status: q(SELECTORS.status),
      shell: q(SELECTORS.shell),
      body: q(SELECTORS.body),
      pageH1: q('[data-page-h1]'),
      seasonTitle: q('[data-season-title]'),
      seasonDates: q('[data-season-dates]'),
      title: q('[data-calendar-title]'),
      subtitle: q('[data-calendar-subtitle]'),
      fajr: q('[data-fajr]'),
      maghrib: q('[data-maghrib]'),
      countdown: q('[data-countdown]'),
      focusLabel: q('[data-focus-label]'),
      focusDate: q('[data-focus-date]'),
      fajrLabel: q('[data-fajr-label]'),
      maghribLabel: q('[data-maghrib-label]'),
      changeCity: q('[data-change-city]'),
      pdf: q('[data-download-pdf]'),
      print: q('[data-print]'),
      share: q('[data-share]'),
      popular: [...root.querySelectorAll('[data-popular-city]')]
    };
  }

  function bindEvents(state, els) {
    els.submit.addEventListener('click', () => resolveAndLoad(els.input.value, state, els));

    els.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        resolveAndLoad(els.input.value, state, els);
      }

      if (event.key === 'Escape') hideResults(els);
    });

    els.input.addEventListener('input', () => {
      clearTimeout(state.searchTimer);
      const q = els.input.value.trim();

      if (q.length < 2) return hideResults(els);

      state.searchTimer = setTimeout(() => autocomplete(q, state, els), 260);
    });

    document.addEventListener('click', (event) => {
      if (!state.root.contains(event.target)) return;
      if (!event.target.closest('[data-city-search-wrap]')) hideResults(els);
    });

    els.popular.forEach(btn => btn.addEventListener('click', () => {
      els.input.value = btn.dataset.popularCity;
      resolveAndLoad(btn.dataset.popularCity, state, els);
    }));

    els.changeCity.addEventListener('click', () => {
      els.input.focus({ preventScroll: false });
      state.root.querySelector('.lnr__search-panel')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    });

    els.print.addEventListener('click', () => window.print());

    els.pdf.addEventListener('click', () => {
      if (!state.city || !state.calendar) return;
      downloadCalendarPdf(state.city, state.calendar);
    });

    els.share.addEventListener('click', () => shareCalendar(state, els));
  }

  async function loadSeasonMeta(state, els) {
    const meta = await apiGet(state.worker + '/meta');
    applySeasonMeta(meta, els);
  }

  function applySeasonMeta(meta, els) {
    if (!meta) return;

    if (els.pageH1) {
      els.pageH1.textContent =
        `Horaires Ramadan ${meta.gregorianYear} en France : Fajr, Iftar et calendrier par ville`;
    }

    els.seasonTitle.textContent = `Ramadan ${meta.gregorianYear} · ${meta.hijriYear} H`;
    updateNationalSeo(meta.gregorianYear);

    els.seasonDates.textContent =
      `Du ${formatFrenchDate(meta.startDate)} au ${formatFrenchDate(meta.endDate)} selon le calendrier prévisionnel. Confirmation religieuse à suivre.`;
  }

  async function autocomplete(query, state, els) {
    try {
      const payload = await apiGet(
        `${state.worker}/cities?q=${encodeURIComponent(query)}`
      );

      renderResults(payload.cities || [], state, els);
    } catch (_) {
      hideResults(els);
    }
  }

  function renderResults(cities, state, els) {
    els.results.innerHTML = '';

    if (!cities.length) return hideResults(els);

    cities.slice(0, 8).forEach(city => {
      const btn = document.createElement('button');

      btn.type = 'button';
      btn.className = 'lnr__result';
      btn.setAttribute('role', 'option');

      btn.innerHTML =
        `<span><strong>${escapeHtml(city.name)}</strong><br><small>${escapeHtml(city.department || '')}</small></span><small>${escapeHtml((city.postcodes || []).slice(0, 2).join(' · '))}</small>`;

      btn.addEventListener('click', () => {
        els.input.value = city.name;
        hideResults(els);
        loadCalendarForCity(city, state, els);
      });

      els.results.appendChild(btn);
    });

    els.results.hidden = false;
    els.input.setAttribute('aria-expanded', 'true');
  }

  function hideResults(els) {
    els.results.hidden = true;
    els.input.setAttribute('aria-expanded', 'false');
  }

  async function resolveAndLoad(query, state, els, options = {}) {
    const clean = String(query || '').trim();

    if (clean.length < 2) {
      return setStatus(els, 'Saisissez une ville ou un code postal.', true);
    }

    hideResults(els);
    setStatus(els, 'Recherche de la commune…');
    els.submit.disabled = true;

    try {
      const payload = await apiGet(
        `${state.worker}/cities?q=${encodeURIComponent(clean)}`
      );

      const city = payload.cities?.[0];

      if (!city) throw new Error('Aucune commune trouvée.');

      els.input.value = city.name;

      await loadCalendarForCity(city, state, els, options);
    } catch (error) {
      setStatus(
        els,
        error.message || 'Impossible de charger cette ville.',
        true
      );
    } finally {
      els.submit.disabled = false;
    }
  }

  async function loadCalendarForCity(city, state, els, options = {}) {
    state.city = city;

    setStatus(els, `Chargement des horaires de ${city.name}…`);

    const key = calendarCacheKey(city);

    let payload;
    let usedCache = false;

    const cached = readCache(key);

    try {
      payload = await apiGet(
        `${state.worker}/ramadan?lat=${encodeURIComponent(city.lat)}&lon=${encodeURIComponent(city.lon)}&city=${encodeURIComponent(city.name)}`
      );

      writeCache(key, payload);
    } catch (error) {
      if (cached?.payload) {
        payload = cached.payload;
        usedCache = true;
      } else {
        throw error;
      }
    }

    state.calendar = payload;

    renderCalendar(city, payload, state, els);

    setStatus(
      els,
      usedCache
        ? 'Calendrier chargé depuis la dernière copie disponible.'
        : ''
    );

    if (options.updateUrl !== false) updateUrl(city.name);
  }

  function renderCalendar(city, payload, state, els) {
    const days = payload.days || [];

    if (!days.length) throw new Error('Calendrier vide.');

    applySeasonMeta({
      gregorianYear: payload.gregorianYear,
      hijriYear: payload.hijriYear,
      startDate: days[0].date,
      endDate: days[days.length - 1].date
    }, els);

    els.title.textContent =
      `Ramadan ${payload.gregorianYear} à ${city.name}`;

    els.subtitle.textContent =
      `${days.length} jours · Ramadan ${payload.hijriYear} H · méthode UOIF France`;

    els.body.innerHTML = '';

    const todayIso = dateInTimeZone(payload.timezone || 'Europe/Paris');
    const todayIndex = days.findIndex(d => d.date === todayIso);
    const focus = todayIndex >= 0 ? days[todayIndex] : days[0];

    days.forEach((day, index) => {
      const tr = document.createElement('tr');
      const isToday = day.date === todayIso;

      tr.dataset.today = String(isToday);
      tr.dataset.lastTen = String(Number(day.hijriDay) >= 21);

      tr.innerHTML = [
        String(day.hijriDay).padStart(2, '0'),
        formatShortDate(day.date),
        day.Fajr,
        day.Sunrise,
        day.Dhuhr,
        day.Asr,
        day.Maghrib,
        day.Isha
      ].map(value => `<td>${escapeHtml(value || '—')}</td>`).join('');

      els.body.appendChild(tr);

      if (index === todayIndex) {
        setTimeout(() => tr.scrollIntoView({ block: 'nearest' }), 0);
      }
    });

    els.fajr.textContent = focus.Fajr || '—';
    els.maghrib.textContent = focus.Maghrib || '—';

    els.focusLabel.textContent =
      todayIndex >= 0 ? `Aujourd’hui · ${city.name}` : 'Premier jour';

    els.focusDate.textContent =
      `${formatFrenchDate(focus.date)} · ${focus.hijriDay} Ramadan ${payload.hijriYear} H`;

    els.fajrLabel.textContent =
      todayIndex >= 0 ? 'Début du jeûne aujourd’hui' : 'Fajr du premier jour';

    els.maghribLabel.textContent =
      todayIndex >= 0 ? 'Iftar aujourd’hui' : 'Iftar du premier jour';

    updateCountdown(
      focus,
      payload.timezone,
      todayIndex >= 0,
      els
    );

    clearInterval(state.countdownTimer);

    state.countdownTimer = setInterval(
      () => updateCountdown(
        focus,
        payload.timezone,
        todayIndex >= 0,
        els
      ),
      60_000
    );

    els.shell.hidden = false;

    updateSeo(city, payload);
  }

  function updateCountdown(day, timeZone, isToday, els) {
    if (!isToday) {
      els.countdown.textContent = 'Calendrier prêt';
      return;
    }

    const now = timePartsInZone(timeZone || 'Europe/Paris');

    const current = now.hour * 60 + now.minute;
    const fajr = timeToMinutes(day.Fajr);
    const maghrib = timeToMinutes(day.Maghrib);

    if (Number.isFinite(fajr) && current < fajr) {
      els.countdown.textContent =
        `Fajr dans ${formatDuration(fajr - current)}`;
    } else if (Number.isFinite(maghrib) && current < maghrib) {
      els.countdown.textContent =
        `Iftar dans ${formatDuration(maghrib - current)}`;
    } else {
      els.countdown.textContent = 'Iftar passé aujourd’hui';
    }
  }

  async function shareCalendar(state, els) {
    const city = state.city;
    const cal = state.calendar;

    if (!city || !cal) return;

    const url = new URL(location.href);
    url.searchParams.set('ville', city.name);

    const data = {
      title: `Horaires Ramadan ${cal.gregorianYear} à ${city.name}`,
      text: `Calendrier Ramadan ${cal.gregorianYear} à ${city.name} — Fajr et Iftar sur LABAYKANUSUK.`,
      url: url.toString()
    };

    try {
      if (navigator.share) {
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(`${data.text} ${data.url}`);
        setStatus(els, 'Lien copié. Vous pouvez maintenant le partager.');
      }
    } catch (_) {}
  }

  function updateSeo(_city, payload) {
    updateNationalSeo(payload.gregorianYear);
  }

  function updateNationalSeo(year) {
    if (!year) return;

    const title =
      `Horaires Ramadan ${year} en France : Fajr et Iftar | LABAYKANUSUK`;

    const description =
      `Consultez gratuitement les horaires du Ramadan ${year} en France : Fajr, Iftar, prières et calendrier complet par ville à enregistrer en PDF.`;

    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
  }

  function upsertMeta(attr, key, value) {
    let tag =
      document.head.querySelector(`meta[${attr}="${key}"]`);

    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attr, key);
      document.head.appendChild(tag);
    }

    tag.setAttribute('content', value);
  }

  function updateUrl(cityName) {
    const url = new URL(location.href);

    url.searchParams.set('ville', cityName);

    history.replaceState({}, '', url);
  }

  async function apiGet(url) {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' }
    });

    let data = null;

    try {
      data = await response.json();
    } catch (_) {}

    if (!response.ok) {
      throw new Error(
        data?.error || `Erreur réseau (${response.status}).`
      );
    }

    return data;
  }

  function setStatus(els, text, isError = false) {
    els.status.textContent = text || '';
    els.status.style.color = isError ? '#8a2634' : '';
  }

  function normalizeWorkerUrl(value) {
    return String(value).trim().replace(/\/$/, '');
  }

  function calendarCacheKey(city) {
    return `lnr:${CACHE_VERSION}:${city.code || city.name}`;
  }

  function readCache(key) {
    try {
      const data = JSON.parse(localStorage.getItem(key));

      if (!data?.payload) return null;

      return {
        ...data,
        fresh: Date.now() - data.savedAt < CALENDAR_CACHE_MS
      };
    } catch (_) {
      return null;
    }
  }

  function writeCache(key, payload) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          savedAt: Date.now(),
          payload
        })
      );
    } catch (_) {}
  }

  function dateInTimeZone(timeZone) {
    const p = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());

    const get = t =>
      p.find(x => x.type === t)?.value;

    return `${get('year')}-${get('month')}-${get('day')}`;
  }

  function timePartsInZone(timeZone) {
    const p = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    }).formatToParts(new Date());

    return {
      hour: Number(
        p.find(x => x.type === 'hour')?.value || 0
      ),
      minute: Number(
        p.find(x => x.type === 'minute')?.value || 0
      )
    };
  }

  function timeToMinutes(value) {
    const match =
      String(value || '').match(/^(\d{1,2}):(\d{2})/);

    return match
      ? Number(match[1]) * 60 + Number(match[2])
      : NaN;
  }

  function formatDuration(minutes) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;

    return h
      ? `${h} h ${String(m).padStart(2, '0')}`
      : `${m} min`;
  }

  function formatShortDate(iso) {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  }

  function formatFrenchDate(iso) {
    if (!iso) return '';

    const [y, m, d] = iso.split('-').map(Number);

    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    }).format(
      new Date(Date.UTC(y, m - 1, d))
    );
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(
      /[&<>'"]/g,
      c => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      }[c])
    );
  }

  function downloadCalendarPdf(city, calendar) {
    const rows = (calendar.days || []).map(day => ({
      n: String(day.hijriDay).padStart(2, '0'),
      date: formatShortDate(day.date),
      fajr: day.Fajr,
      sunrise: day.Sunrise,
      dhuhr: day.Dhuhr,
      asr: day.Asr,
      maghrib: day.Maghrib,
      isha: day.Isha
    }));

    const pdfBytes = buildSimplePdf({
      city: city.name,
      year: calendar.gregorianYear,
      hijriYear: calendar.hijriYear,
      rows
    });

    const blob = new Blob([pdfBytes], {
      type: 'application/pdf'
    });

    const a = document.createElement('a');

    a.href = URL.createObjectURL(blob);
    a.download =
      `calendrier-ramadan-${calendar.gregorianYear}-${slugify(city.name)}-labaykanusuk.pdf`;

    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  function buildSimplePdf({
    city,
    year,
    hijriYear,
    rows
  }) {
    const perPage = 18;
    const pages = [];

    for (let i = 0; i < rows.length; i += perPage) {
      pages.push(rows.slice(i, i + perPage));
    }

    const objects = [];
    const pageIds = [];

    const fontRegularId = 3;
    const fontBoldId = 4;

    objects[fontRegularId] =
      '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>';

    objects[fontBoldId] =
      '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>';

    pages.forEach((pageRows, pageIndex) => {
      const pageId = 5 + pageIndex * 2;
      const streamId = pageId + 1;

      pageIds.push(pageId);

      const stream = pdfPageStream(
        city,
        year,
        hijriYear,
        pageRows,
        pageIndex + 1,
        pages.length
      );

      objects[streamId] =
        `<< /Length ${latin1Length(stream)} >>\nstream\n${stream}\nendstream`;

      objects[pageId] =
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 ${fontRegularId} 0 R /F2 ${fontBoldId} 0 R >> >> /Contents ${streamId} 0 R >>`;
    });

    objects[1] =
      '<< /Type /Catalog /Pages 2 0 R >>';

    objects[2] =
      `<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`;

    const maxId =
      Math.max(...Object.keys(objects).map(Number));

    let pdf =
      '%PDF-1.4\n%âãÏÓ\n';

    const offsets = [0];

    for (let id = 1; id <= maxId; id++) {
      if (!objects[id]) continue;

      offsets[id] = latin1Length(pdf);

      pdf +=
        `${id} 0 obj\n${objects[id]}\nendobj\n`;
    }

    const xrefOffset =
      latin1Length(pdf);

    pdf +=
      `xref\n0 ${maxId + 1}\n0000000000 65535 f \n`;

    for (let id = 1; id <= maxId; id++) {
      const off = offsets[id] || 0;

      pdf +=
        `${String(off).padStart(10, '0')} 00000 ${objects[id] ? 'n' : 'f'} \n`;
    }

    pdf +=
      `trailer\n<< /Size ${maxId + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

    return toLatin1Bytes(pdf);
  }

  function pdfPageStream(
    city,
    year,
    hijriYear,
    rows,
    pageNo,
    pageCount
  ) {
    const safeCity = pdfText(city);
    const lines = [];

    lines.push(
      '0.141 0 0.239 rg 0 762 595 80 re f'
    );

    lines.push('1 1 1 rg');

    lines.push(
      `BT /F2 20 Tf 36 806 Td (${pdfText('LABAYKANUSUK')}) Tj ET`
    );

    lines.push(
      `BT /F1 10 Tf 36 786 Td (${pdfText(`Calendrier Ramadan ${year} · ${hijriYear} H`)}) Tj ET`
    );

    lines.push('0.18 0.42 0.333 rg');

    lines.push(
      `BT /F2 18 Tf 36 730 Td (${safeCity}) Tj ET`
    );

    lines.push('0.141 0 0.239 rg');

    lines.push(
      `BT /F1 9 Tf 36 712 Td (${pdfText(`Horaires indicatifs · Methode UOIF France · Page ${pageNo}/${pageCount}`)}) Tj ET`
    );

    lines.push(
      '0.95 0.95 0.95 rg 30 675 535 24 re f'
    );

    lines.push('0.141 0 0.239 rg');

    lines.push(
      `BT /F2 8 Tf 36 683 Td (${pdfText('Jour   Date         Fajr    Lever   Dhuhr   Asr     Iftar   Isha')}) Tj ET`
    );

    let y = 655;

    rows.forEach((r, idx) => {
      if (idx % 2) {
        lines.push(
          `0.985 0.98 0.97 rg 30 ${y - 6} 535 22 re f`
        );
      }

      lines.push(
        '0.12 0.10 0.13 rg'
      );

      const row =
        `${r.n.padEnd(6)} ${r.date.padEnd(12)} ${padTime(r.fajr)} ${padTime(r.sunrise)} ${padTime(r.dhuhr)} ${padTime(r.asr)} ${padTime(r.maghrib)} ${padTime(r.isha)}`;

      lines.push(
        `BT /F1 8 Tf 36 ${y} Td (${pdfText(row)}) Tj ET`
      );

      y -= 27;
    });

    lines.push(
      '0.35 0.32 0.36 rg'
    );

    lines.push(
      `BT /F1 7.5 Tf 36 75 Td (${pdfText('Les horaires peuvent differer selon les reglages locaux. Suivez votre mosquee ou autorite de reference en cas d ecart.')}) Tj ET`
    );

    lines.push(
      `BT /F1 7.5 Tf 36 61 Td (${pdfText('Le debut effectif de Ramadan reste soumis a l annonce religieuse suivie. Calendrier gratuit : labaykanusuk.com')}) Tj ET`
    );

    lines.push(
      '0.839 0.655 0.173 rg 36 47 523 2 re f'
    );

    return lines.join('\n');
  }

  function padTime(v) {
    return String(v || '--:--')
      .slice(0, 5)
      .padEnd(8);
  }

  function pdfText(value) {
    return String(value || '')
      .replace(/[–—]/g, '-')
      .replace(/’/g, "'")
      .replace(/œ/g, 'oe')
      .replace(/Œ/g, 'OE')
      .replace(/[^\x20-\xFF]/g, '?')
      .replace(/\\/g, '\\\\')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)');
  }

  function toLatin1Bytes(str) {
    const out = new Uint8Array(str.length);

    for (let i = 0; i < str.length; i++) {
      out[i] =
        str.charCodeAt(i) & 255;
    }

    return out;
  }

  function latin1Length(str) {
    return str.length;
  }

  function slugify(value) {
    return String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }
})();